import config from '@/config'
import { sortOptions } from '@/utils/generalFunctions'
import AppState, { ISelectOption } from '../../states/app'
import { requests } from './BaseService'
import { WhatsappTemplateService } from './WhatsappService'
const emailBuilderEndpoint = `/emails/builder/all`

export const TemplateService = {
  async getEmailTemplates(skip = 0, limit = 2000): Promise<ISelectOption[]> {
    const [emailSnippets, emailBuilderTemplates] = await Promise.all([
      getTemplates('email', skip, limit),
      getEmailBuilderTemplates(),
    ])

    return sortOptions(
      emailSnippets
        .concat(emailBuilderTemplates as any)
        .map(t => ({ ...t, value: t.id, label: t.name || 'No Name' })),
      'label'
    )
  },

  async getSMSTemplates(skip = 0, limit = 2000): Promise<ISelectOption[]> {
    const templates = await getTemplates('sms', skip, limit)
    return sortOptions(
      templates.map(t => ({ ...t, value: t.id, label: t.name })),
      'label'
    )
  },

  async getWhatsAppTemplates(): Promise<ISelectOption[]> {
    try {
      // Fetch both user templates and default templates in parallel
      const [userTemplatesResponse, defaultTemplatesResponse] = await Promise.all([
        WhatsappTemplateService.getUserTemplates(AppState.locationId),
        WhatsappTemplateService.getDefaultTemplates(AppState.locationId)
      ])

      const userTemplatesData = userTemplatesResponse.data || []
      const defaultTemplatesData = defaultTemplatesResponse.data.templates || []
      
      // Transform user templates to dropdown format
      const allUserTemplates = userTemplatesData.map((template: any) => ({
        ...template,
        value: template.id,
        label: template.name,
        source: 'user'
      }))

      // Separate approved and pending user templates
      const approvedUserTemplates = allUserTemplates.filter(template => template.status === 'APPROVED')
      const pendingUserTemplates = allUserTemplates.filter(template => template.status === 'PENDING')

      // Sort templates within each section
      const sortedApproved = sortOptions(approvedUserTemplates, 'label') as ISelectOption[]
      const sortedPending = sortOptions(pendingUserTemplates, 'label').map(template => ({
        ...template,
        disabled: true,
        type: 'pending'
      })) as ISelectOption[]

      // Transform default templates to dropdown format
      const defaultTemplateOptions: ISelectOption[] = defaultTemplatesData.map(template => ({
        value: template.templateId || template.id,
        label: template.templateName || template.name || `Default Template (${template.notificationType})`,
        source: 'default',
        type: 'default',
        disabled: false,
        template: template
      }))

      // Combine all templates with sections
      const allTemplates: ISelectOption[] = []

      // Add default templates section
      if (defaultTemplateOptions.length > 0) {
        allTemplates.push({
          value: 'default-templates-header',
          label: 'Default Templates',
          disabled: true,
          type: 'section-header'
        } as ISelectOption)
        allTemplates.push(...sortOptions(defaultTemplateOptions, 'label'))
      }

      // Add user templates sections
      if (sortedApproved.length > 0) {
        allTemplates.push({
          value: 'user-approved-header',
          label: 'Your Templates (Approved)',
          disabled: true,
          type: 'section-header'
        } as ISelectOption)
        allTemplates.push(...sortedApproved)
      }

      if (sortedPending.length > 0) {
        allTemplates.push({
          value: 'user-pending-header',
          label: 'Your Templates (Pending)',
          disabled: true,
          type: 'section-header'
        } as ISelectOption)
        allTemplates.push(...sortedPending)
      }

      return allTemplates
    } catch (error) {
      console.error('Error fetching WhatsApp templates:', error)
      return []
    }
  },
}

async function getTemplates(type: 'email' | 'sms', skip = 0, limit = 1000) {
  try {
    const response = (await requests().get(
      `${config.restApiUrl}/locations/${AppState.locationId}/templates`,
      {
        params: {
          type,
          skip,
          limit,
        },
      }
    ))
    return response.data.templates.map(t => ({
      ...t,
      name: type === 'sms' ? t.name : `${t.name} (HTML snippet)`,
      source: 'other',
    }))
  } catch (e) {
    console.warn(`Error Fetching Templates ${type}`)
    return []
  }
}

async function getEmailBuilderTemplates() {
  const fields = [
    'versionHistory',
    'builderVersion',
    'fromAddress',
    'fromName',
    'subjectLine',
    'previewText',
    'locationId',
    'type',
    'id',
    'archived',
    'emailData',
    'downloadUrl',
    'dateAdded',
    'dateUpdated',
    'subjectLine',
  ].join(',')

  try {
    const response = await requests().get(
      `${config.restApiUrl}${emailBuilderEndpoint}`,
      {
        params: {
          locationId: AppState.locationId,
          deleted: false,
          fields,
        },
      }
    )

    const emailTemplates = response?.data as Array<{
      downloadUrl: string
      dateAdded: string
      dateUpdated: string
      [key: string]: string | boolean | number
    }>

    return emailTemplates.map(template => ({
      ...template,
      type: 'email',
      source: 'email-builder',
      previewUrl: template.downloadUrl,
      createdAt: template.dateAdded,
      updatedAt: template.dateUpdated,
      subject: template.subjectLine,
    }))
  } catch (error) {
    console.warn('Error Fetching Templates: Email Builder', error)
    return []
  }
}
